import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import EnumSelect from 'components/field/EnumSelect';
import SearchInput from 'components/field/SearchInput';
import NoRecordsFound from 'components/global/NoRecordsFound';
import Skeleton from 'components/global/Skeleton';
import SubformHeader from 'components/global/SubformHeader';
import Tooltip from 'components/global/Tooltip';

import useDebounce from 'hooks/useDebounce';
import useLocale from 'hooks/useLocale';
import useUpdateEffect from 'hooks/useUpdateEffect';

import { ENUMS } from 'services/consts';

import { faShippingFast } from '@fortawesome/pro-solid-svg-icons';

import * as S from './MoreConnection.styled';
import MoreConnectionCard from './MoreConnectionCard';
import { getMorePartnersList } from './MorePartnersSlice.list.js';

/**
 *
 * @author @muhammedburakak
 */

const MoreConnection = () => {
	const { t } = useTranslation();
	const { LanguageCode } = useLocale();
	const morePartnersList = useMorePartnersList();

	const [selectedCountries, setSelectedCountries] = useState(null);
	const [loading, setLoading] = useState(true);
	const [selectedServiceTypes, setSelectedServiceTypes] = useState(null);
	const [filteredList, setFilteredList] = useState([]);
	const [searchValue, setSearchValue] = useState(null);

	const debouncedValue = useDebounce(searchValue, 500);

	useEffect(() => {
		if (morePartnersList.length > 0) {
			setFilteredList(morePartnersList);
			setLoading(false);
		}
	}, [morePartnersList]);

	useUpdateEffect(() => {
		let data = morePartnersList;

		if (debouncedValue?.length > 0) {
			data = data?.filter((item) => {
				const localeName = item.name?.toLocaleLowerCase();
				const localeNameAR = item.nameAR?.toLocaleLowerCase();
				const localeValue = debouncedValue?.toLocaleLowerCase();

				return (
					localeName?.includes(localeValue) ||
					localeNameAR?.includes(localeValue) ||
					localeName?.split(' ').join('').includes(localeValue) ||
					localeNameAR?.split(' ').join('').includes(localeValue)
				);
			});
		}

		if (selectedCountries?.length > 0) {
			data = data?.filter(
				(item) =>
					item.countries && item.countries.split(',').some((country) => selectedCountries.includes(country))
			);
		}

		if (selectedServiceTypes?.length > 0) {
			data = data?.filter(
				(item) =>
					item.serviceTypes &&
					item.serviceTypes.split(',').some((serviceType) => selectedServiceTypes.includes(serviceType))
			);
		}

		setFilteredList(data);
	}, [selectedCountries, selectedServiceTypes, debouncedValue]);

	const scrollRef = React.useRef(null);

	return (
		<S.FlexCard>
			<SubformHeader
				lordIcon="https://storage.googleapis.com/tryoto-public/lottie-files-compressed/shippingPartners-icon-new.json.gz"
				icon={faShippingFast}
				trigger="hover"
				boldTitle="connectAShippingPartner"
				lightTitle="connectAShippingPartnerSubtitle"
				className="table-lord-icon"
			/>
			<S.InputContainer>
				<SearchInput
					setValue={setSearchValue}
					value={searchValue}
					variant="grey"
					placeholder="searchKeyword"
					fullWidth
				/>
			</S.InputContainer>
			<S.ExpandedFilterContainer>
				<EnumSelect
					labelPrefix="country."
					placeholder="country"
					enumName={ENUMS.DC_COUNTRIES}
					value={selectedCountries}
					setValue={setSelectedCountries}
					multipleSelection
				/>
				<EnumSelect
					placeholder="serviceType"
					enumName={ENUMS.DC_SERVICE_TYPES}
					value={selectedServiceTypes}
					setValue={setSelectedServiceTypes}
					multipleSelection
				/>
			</S.ExpandedFilterContainer>
			{!loading ? (
				filteredList.length > 0 ? (
					<S.PartnersContainer ref={scrollRef}>
						{filteredList?.map((partner) => (
							<div
								style={{
									marginBottom: 'var(--margin-2)'
								}}
								key={partner.ID}>
								<Tooltip
									title={
										LanguageCode === 'arabicValue'
											? (partner.nameAR ?? partner.name)
											: `${partner.name} - ${t(partner.companyType)}`
									}
									placement="bottom"
									scrollRef={scrollRef}>
									<MoreConnectionCard data={partner} />
								</Tooltip>
							</div>
						))}
					</S.PartnersContainer>
				) : (
					<NoRecordsFound />
				)
			) : (
				<S.LoadingContainer>
					{[...Array(80)].map((_, i) => (
						<Skeleton key={i} width="11.5rem" height="11.5rem" />
					))}
				</S.LoadingContainer>
			)}
		</S.FlexCard>
	);
};

export const useMorePartnersList = () => {
	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(getMorePartnersList());
	}, []);

	const MorePartnersList = useSelector((state) => state?.morePartners);

	return MorePartnersList;
};

export default MoreConnection;
